import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, App, Button, Input, Popconfirm, Spin } from 'antd';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { KcExclusion } from '../types';
import { creatorName, formatDateLocal, kcErrorMessage, normalizeExclusions } from '../utils';
import styles from '../style.module.scss';

interface Props {
  /** Tăng lên khi trang vừa thêm / gỡ loại trừ từ bảng → tải lại danh sách. */
  refreshKey: number;
  /** Vừa thêm / gỡ ở thẻ này → trang tải lại bảng tháng. */
  onChanged: () => void;
}

/**
 * Danh sách loại trừ (áp cho mọi tháng, lưu theo UID). ADMIN và MOD đều thêm / gỡ được.
 * Creator trong danh sách vẫn được ghi mốc và phân tích, chỉ không lên Trang chủ app / push.
 */
const ExclusionsCard = ({ refreshKey, onChanged }: Props) => {
  const { message } = App.useApp();
  const [items, setItems] = useState<KcExclusion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState('');
  const [note, setNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    AxiosUtils.get(API.kcMilestonesAdminExclusions)
      .then((res: any) => {
        if (!cancelled) setItems(normalizeExclusions(res?.data));
      })
      .catch((err) => {
        if (!cancelled) setLoadError(kcErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, reloadKey]);

  const reloadList = () => setReloadKey((key) => key + 1);

  const add = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      const body: { query: string; note?: string } = { query: trimmed };
      if (note.trim()) body.note = note.trim();
      const res: any = await AxiosUtils.post(API.kcMilestonesAdminExclusions, body);
      const added = normalizeExclusions(res?.data ? [res.data] : [])[0];
      message.success(
        `Đã loại trừ ${added ? creatorName(added.username, added.tiktokUid) : trimmed} ở mọi tháng.`
      );
      setQuery('');
      setNote('');
      reloadList();
      onChanged();
    } catch (err) {
      message.error(kcErrorMessage(err));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (item: KcExclusion) => {
    setRemoving(item.tiktokUid);
    try {
      await AxiosUtils._delete(
        `${API.kcMilestonesAdminExclusions}/${encodeURIComponent(item.tiktokUid)}`,
        undefined
      );
      message.success(`Đã gỡ ${creatorName(item.username, item.tiktokUid)} khỏi danh sách loại trừ.`);
      reloadList();
      onChanged();
    } catch (err) {
      message.error(kcErrorMessage(err));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <section className={styles.exclusions} aria-labelledby="kc-exclusions-title">
      <div className={styles.configHead}>
        <h2 id="kc-exclusions-title" className={styles.configTitle}>
          Danh sách loại trừ (áp cho mọi tháng)
        </h2>
      </div>
      <p className={styles.help}>
        Creator trong danh sách vẫn được ghi mốc và xem trong phân tích, nhưng không hiện trên
        Trang chủ app và không nhận push. Lưu theo UID nên Creator đổi TikTok ID vẫn bị loại trừ.
      </p>

      <div className={styles.exclusionForm}>
        <Input
          className={styles.exclusionQuery}
          placeholder="@TikTok ID hoặc UID"
          aria-label="@TikTok ID hoặc UID cần loại trừ"
          value={query}
          maxLength={80}
          disabled={adding}
          onChange={(event) => setQuery(event.target.value)}
          onPressEnter={add}
        />
        <Input
          className={styles.exclusionNote}
          placeholder="Ghi chú (không bắt buộc)"
          aria-label="Ghi chú loại trừ"
          value={note}
          maxLength={255}
          disabled={adding}
          onChange={(event) => setNote(event.target.value)}
          onPressEnter={add}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={adding}
          disabled={!query.trim()}
          onClick={add}
        >
          Thêm
        </Button>
      </div>

      {loadError && (
        <Alert
          className={styles.section}
          type="error"
          showIcon
          message="Chưa tải được danh sách loại trừ"
          description={loadError}
          action={
            <Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={reloadList}>
              Tải lại
            </Button>
          }
        />
      )}

      {loading && items.length === 0 ? (
        <div className={styles.empty}>
          <Spin size="small" /> Đang tải…
        </div>
      ) : items.length === 0 ? (
        !loadError && <p className={styles.empty}>Chưa có Creator nào trong danh sách loại trừ.</p>
      ) : (
        <ul className={styles.exclusionList} aria-label="Creator đang bị loại trừ">
          {items.map((item) => {
            const name = creatorName(item.username, item.tiktokUid);
            return (
              <li key={item.tiktokUid} className={styles.exclusionItem}>
                <div className={styles.exclusionMain}>
                  <span className={styles.personName}>{name}</span>
                  <span className={styles.uid}>UID {item.tiktokUid}</span>
                  {item.note && <span className={styles.small}>{item.note}</span>}
                  <span className={styles.personSub}>
                    Thêm bởi {item.createdByName || '—'} · {formatDateLocal(item.createdAt)}
                  </span>
                </div>
                <Popconfirm
                  title={`Gỡ ${name} khỏi danh sách loại trừ?`}
                  description="Creator sẽ lại được chúc mừng trên Trang chủ app nếu đủ mốc."
                  okText="Gỡ"
                  cancelText="Huỷ"
                  onConfirm={() => remove(item)}
                >
                  <Button size="small" loading={removing === item.tiktokUid}>
                    Gỡ
                  </Button>
                </Popconfirm>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default ExclusionsCard;
